import React, { useEffect, useState } from "react";
import axios from "axios";

/**
 * Client portfolio grid — DB-backed via the CMS portfolio endpoint.
 *
 * Reads from `GET /api/cms/portfolio/` (published items only) so any
 * additions/edits via `/admin/portfolio` (or the API directly) appear
 * live on the homepage without a redeploy.
 *
 * Fields consumed per item:
 *   - slug (used in the data-testid)
 *   - title (display name)
 *   - industry (overline tag)
 *   - hero_image_url (path, /case-studies/<slug>.webp by default)
 *   - alt_text (a11y + SEO description)
 *   - url (optional — when present, the whole card becomes a link)
 *
 * Static FALLBACK list is kept so older deployments without the CMS row
 * still render the same grid (24 entries matching what was hardcoded
 * pre-iteration-97). The fallback is ONLY used if the API call fails or
 * returns zero items — once CMS rows exist they always win.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INITIAL = 6;

const FALLBACK_CASES = [
  { slug: "cs-fintech",       title: "Fintech Dashboard",        industry: "Fintech",         hero_image_url: "/case-studies/cs-fintech.webp",        alt_text: "Fintech analytics dashboard mockup with charts and balance cards" },
  { slug: "cs-skincare",      title: "Premium Skincare Brand",   industry: "Beauty",          hero_image_url: "/case-studies/cs-skincare.webp",       alt_text: "Premium skincare ecommerce homepage with product hero" },
  { slug: "cs-fitness",       title: "Boutique Fitness Studio",  industry: "Fitness",         hero_image_url: "/case-studies/cs-fitness.webp",        alt_text: "Boutique fitness studio booking website with class schedule" },
  { slug: "cs-architect",     title: "Architecture Studio",      industry: "Architecture",    hero_image_url: "/case-studies/cs-architect.webp",      alt_text: "Architecture studio portfolio site with project gallery" },
  { slug: "cs-law-firm",      title: "Boutique Law Firm",        industry: "Legal",           hero_image_url: "/case-studies/cs-law-firm.webp",       alt_text: "Boutique law firm website with practice areas and consultation CTA" },
  { slug: "cs-medical",       title: "Medical Practice",         industry: "Healthcare",      hero_image_url: "/case-studies/cs-medical.webp",        alt_text: "Medical practice website with appointment booking flow" },
];

const CaseCard = ({ c, eager = false }) => {
  const inner = (
    <>
      <div className="relative aspect-[16/10] bg-ink-700 overflow-hidden">
        <img
          src={`${process.env.PUBLIC_URL || ""}${c.hero_image_url || `/case-studies/${c.slug}.webp`}`}
          alt={c.alt_text || c.title}
          title={`${c.title} — ${c.industry}`}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          width="1280"
          height="800"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
      </div>
      <figcaption className="absolute bottom-0 inset-x-0 p-5">
        <div className="text-[10px] uppercase tracking-[0.28em] text-gold/90">{c.industry}</div>
        <div className="font-display text-lg md:text-xl text-white mt-1">{c.title}</div>
      </figcaption>
    </>
  );

  const base = "bk-card rounded-2xl overflow-hidden group relative block";
  if (c.url) {
    return (
      <a
        href={c.url}
        target={c.url.startsWith("http") ? "_blank" : undefined}
        rel={c.url.startsWith("http") ? "noopener noreferrer" : undefined}
        data-testid={`case-card-${c.slug}`}
        className={base}
      >
        {inner}
      </a>
    );
  }
  return (
    <figure data-testid={`case-card-${c.slug}`} className={base}>
      {inner}
    </figure>
  );
};

export default function UseCases() {
  const [cases, setCases] = useState(FALLBACK_CASES);
  const [shown, setShown] = useState(INITIAL);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/cms/portfolio/`, { timeout: 8000 });
        const items = r.data?.items || [];
        if (!cancelled && items.length > 0) setCases(items);
      } catch {
        // Network or 404 — quietly stay on the fallback list.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = cases.slice(0, shown);
  const hasMore = shown < cases.length;

  return (
    <section id="use-cases" data-testid="use-cases" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[11px] uppercase tracking-[0.28em] text-gold/80">Use Cases</span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl">
            Proof, <span className="bk-gold-text italic">Not Promises</span>
          </h2>
          <p className="mt-5 text-white/65 leading-relaxed">
            Hear directly from clients about the shifts in clarity, conversions, and momentum after implementation.
          </p>
        </div>

        <div
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
          data-testid="use-cases-grid"
          data-loading={loading ? "true" : "false"}
        >
          {visible.map((c, i) => (
            <CaseCard key={c.slug || c.id} c={c} eager={i < 3} />
          ))}
        </div>

        {hasMore ? (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => setShown((s) => s + INITIAL)}
              data-testid="load-more-cases"
              className="text-xs uppercase tracking-[0.22em] font-semibold px-7 py-3.5 rounded-full border border-white/15 hover:border-gold hover:text-gold transition-colors text-white/80"
            >
              Load More Cases · {cases.length - shown} more
            </button>
          </div>
        ) : (
          <div className="mt-10 text-center text-xs uppercase tracking-[0.22em] text-white/45">
            Showing all {cases.length} client builds
          </div>
        )}
      </div>
    </section>
  );
}
